import { useMemo, useState } from "react";
import CmsLayout from "@layouts/CmsLayout";
import ActionStepMap from "@components/mappings/ActionStepMap";
import IndexTableMap from "@components/mappings/IndexTableMap";
import ConfirmDeleteModal from "@components/modals/ConfirmDeleteModal";
import useLanguages from "@hooks/languages/useLanguages";
import { filterRows, paginateRows } from "@utils/indexTable";
import LanguagesAdd from "@languages/add";
import LanguagesUpdate from "@languages/update";

function LanguagesPage() {
  const [action, setAction] = useState("index");
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, item: null });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 6;

  const {
    languages,
    loading,
    submitting,
    error,
    success,
    createLanguage,
    updateLanguage,
    removeLanguage,
  } = useLanguages();

  const actionTitle = useMemo(() => {
    if (action === "update") return "Update language";
    return "Create language";
  }, [action]);

  function goBackToIndex() {
    setAction("index");
    setSelectedLanguage(null);
  }

  const filtered = useMemo(
    () =>
      filterRows(languages, search, (language) => {
        const iso = language.lng_iso || language.iso || "";
        const name = language.lng_name || language.name || "";
        return `${iso} ${name}`;
      }),
    [languages, search],
  );

  const pagination = useMemo(
    () => paginateRows(filtered, page, pageSize),
    [filtered, page],
  );

  const columns = useMemo(
    () => [
      {
        key: "iso",
        label: "ISO",
        render: (language) => language.lng_iso || language.iso,
      },
      {
        key: "name",
        label: "Name",
        render: (language) => language.lng_name || language.name,
      },
      {
        key: "actions",
        label: "Actions",
        render: (language) => (
          <div className="table-actions">
            <button
              type="button"
              className="button-link"
              onClick={() => {
                setSelectedLanguage(language);
                setAction("update");
              }}
            >
              Edit
            </button>
            <button
              type="button"
              className="button-link button-danger"
              onClick={() => {
                setDeleteModal({ isOpen: true, item: language });
              }}
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  const actionButtons = useMemo(
    () => [
      {
        key: "add",
        label: "Add",
        active: action === "add",
        onClick: () => setAction("add"),
      },
    ],
    [action],
  );

  return (
    <CmsLayout activeModule="languages" title="Languages">
      {error ? <p className="message-error">{error}</p> : null}
      {success ? <p className="message-ok">{success}</p> : null}
      {loading ? <p>Loading languages...</p> : null}
      {action === "index" ? (
        <IndexTableMap
          title="Languages"
          actionButtons={actionButtons}
          columns={columns}
          rows={pagination.pageRows}
          rowKey={(language) => String(language.lng_id || language.id)}
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          page={pagination.page}
          totalPages={pagination.totalPages}
          onPrevPage={() => setPage((prev) => Math.max(1, prev - 1))}
          onNextPage={() =>
            setPage((prev) => Math.min(pagination.totalPages, prev + 1))
          }
          emptyText="No languages found."
        />
      ) : null}

      {action !== "index" ? (
        <ActionStepMap title={actionTitle} onBack={goBackToIndex}>
          {action === "add" ? (
            <LanguagesAdd
              onCreate={async (payload) => {
                await createLanguage(payload);
                goBackToIndex();
              }}
              submitting={submitting}
            />
          ) : null}
          {action === "update" ? (
            <LanguagesUpdate
              language={selectedLanguage}
              onUpdate={async (id, payload) => {
                await updateLanguage(id, payload);
                goBackToIndex();
              }}
              onCancel={goBackToIndex}
              submitting={submitting}
            />
          ) : null}
        </ActionStepMap>
      ) : null}

      <ConfirmDeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, item: null })}
        submitting={submitting}
        title="Delete language"
        description="You are about to delete this language."
        preview={
          <p>
            <strong>
              {deleteModal.item?.lng_name || deleteModal.item?.name || "N/A"}
            </strong>{" "}
            ({deleteModal.item?.lng_iso || deleteModal.item?.iso || "N/A"})
          </p>
        }
        onConfirm={async () => {
          const id = deleteModal.item?.lng_id || deleteModal.item?.id;
          if (!id) return;
          await removeLanguage(String(id));
          setDeleteModal({ isOpen: false, item: null });
        }}
      />
    </CmsLayout>
  );
}

export default LanguagesPage;
